<template>
  <div v-if="display === 'status'">
    <el-tooltip v-if="statusTooltip" :content="statusTooltip" placement="top">
      <span>{{ statusText }}</span>
    </el-tooltip>
    <span v-else>{{ statusText }}</span>
  </div>
  <div v-else>
    <el-button v-if="allowed && available" type="text" :loading="submitting" @click="submit">
      {{ closing ? '平仓' : '回收' }}
    </el-button>
    <span v-else>--</span>
  </div>
</template>

<script>
import { hasPerm } from 'common/permission'
import moment from 'moment'
import bonusApi from '../BonusStatistic/api'
import couponApi from '../CouponStatistic/api'

export default {
  props: {
    row: { type: Object, required: true },
    closing: { type: Boolean, default: false },
    deduct: { type: Boolean, default: false },
    display: { type: String, default: 'action', validator: (value) => ['status', 'action'].includes(value) },
  },
  data() { return { submitting: false } },
  computed: {
    allowed() {
      const permission = this.closing ? 'tenant:welfare_position_experience_stats:close' : 'tenant:welfare_bonus_stats:recycle'
      return hasPerm(this.$store.state.userInfo || {}, permission)
    },
    available() { return this.closing ? this.row.canClose : this.row.canRecycle },
    statusText() {
      if (!this.closing) {
        const labels = {
          NONE: '--', PROCESSING: '回收中', SETTLING: '清算中',
          SUCCESS: '回收成功', USED: '已使用完', FAILED: '回收失败', UNKNOWN: '状态未知',
          PARTIAL: '部分回收',
        }
        return labels[this.row.recycleStatus] || '状态未知'
      }
      const labels = {
        NONE: '--', PROCESSING: '平仓中', SUCCESS: '平仓成功',
        SETTLING: '已平仓，结算中', SETTLEMENT_FAILED: '已平仓，结算失败',
        FAILED: '平仓失败', UNKNOWN: '状态未知',
      }
      return labels[this.row.closeStatus] || '状态未知'
    },
    statusTooltip() {
      if (this.closing) {
        const reasons = {
          MANUAL: '手动平仓', TAKE_PROFIT: '止盈', STOP_LOSS: '止损',
          MOVE_PROFIT: '移动止盈', LIQUIDATION: '强平', POSITION_EXPIRED: '到期自动平仓',
        }
        const parts = []
        if (this.row.closeMessage) parts.push(this.row.closeMessage)
        if (this.row.closeReason) parts.push(`平仓原因：${reasons[this.row.closeReason] || this.row.closeReason}`)
        if (this.row.closeTime) {
          const time = moment(Number(this.row.closeTime))
          if (time.isValid()) parts.push(`平仓时间：${time.utcOffset(480).format('YYYY-MM-DD HH:mm:ss')}（UTC+8）`)
        }
        return parts.join('；')
      }
      if (this.deduct) {
        const reasons = {
          MANUAL: '手动回收', EXPIRE: '到期自动回收',
          RATE_CONFLICT: '抵扣比例变更回收', TRANSFER: '转出回收',
          FAMILY_CONFLICT: '切换券类型回收',
        }
        const parts = []
        if (this.row.recycleMessage) parts.push(this.row.recycleMessage)
        if (this.row.recycleReason) {
          const names = this.row.recycleReason.split(',').filter(Boolean).map((reason) => reasons[reason] || reason)
          if (names.length) parts.push(`原因：${names.join('、')}`)
        }
        if (this.row.recycleTime) {
          const time = moment(Number(this.row.recycleTime))
          if (time.isValid()) parts.push(`最近回收时间：${time.utcOffset(480).format('YYYY-MM-DD HH:mm:ss')}（UTC+8）`)
        }
        return parts.join('；')
      }
      if (this.row.recycleStatus === 'FAILED') return this.row.recycleMessage || '回收失败'
      if (!['SUCCESS', 'USED', 'PROCESSING', 'SETTLING'].includes(this.row.recycleStatus)) return ''
      const reasons = {
        MANUAL: '手动回收', EXPIRE: '到期自动回收',
        DEDUCTED_OUT: '体验金使用完清算', LIQUIDATION: '强平后自动回收',
        FAMILY_CONFLICT: '切换券类型回收',
      }
      const parts = []
      if (reasons[this.row.recycleReason]) parts.push(`原因：${reasons[this.row.recycleReason]}`)
      if (this.row.recycleStatus === 'SUCCESS' && this.row.recycleTime) {
        const time = moment(Number(this.row.recycleTime))
        if (time.isValid()) parts.push(`回收时间：${time.utcOffset(480).format('YYYY-MM-DD HH:mm:ss')}（UTC+8）`)
      }
      return parts.join('；')
    },
  },
  methods: {
    async submit() {
      if (this.submitting || !this.allowed || !this.available) return
      const action = this.closing ? '平仓' : '回收'
      try {
        await this.$confirm(`确认对该记录执行手动${action}吗？操作后不可撤销，请谨慎操作。`, '提示', {
          confirmButtonText: '确认', cancelButtonText: '取消', type: 'warning',
        })
      } catch (_) { return }
      this.submitting = true
      try {
        const result = this.closing ? await couponApi.closePosition(this.row.positionId) : await bonusApi.recycle(this.row)
        if (!result || result.code !== 0 || !result.data) throw new Error((result && result.msg) || '请求结果未确认，请刷新列表查看')
        if (result.data.status === 'SUCCESS') this.$message.success(`${action}成功`)
        else this.$message.info(`${action}请求已受理，请在列表查看处理结果。`)
      } catch (error) {
        this.$message.error((error && error.message) || '请求结果未确认，请刷新列表查看后重试')
      } finally {
        this.submitting = false
        this.$emit('refresh')
      }
    },
  },
}
</script>
